/**
 * <PartTransformGroup> — wraps children with a per-part 3D pose group.
 *
 * The renderer composes orientation ⊗ trackTransform ⊗ partTransformation
 * ⊗ chain. The part transform sits inside the per-XPU `<group>` (which
 * carries the trackTransform) and applies to everything anchored to the
 * part: modules, guiding rails, movers, stations, areas, dimensions,
 * info bars, stop-position ghost movers, stator heatmap.
 *
 * Used both by `<ProcessingUnitRoot>` (for modules + movers) and by the
 * per-part path overlays in `<XtsScene>`. Centralising the math here
 * keeps every component agreeing on the same transform composition.
 */

import React from 'react';
import type { PartTransformation } from '../../types.js';

const DEG2RAD = Math.PI / 180;

interface PartTransformGroupProps {
  /** Per-part transform; `undefined` / `null` renders as identity. */
  partTransformation?: PartTransformation | null;
  /** Group name (debug aid in the Three.js scene graph). */
  name?: string;
  children?: React.ReactNode;
}

export const PartTransformGroup: React.FC<PartTransformGroupProps> = ({
  partTransformation,
  name,
  children,
}) => {
  const pos = partTransformation?.offsetMm ?? [0, 0, 0];
  const rot = partTransformation?.rotationDegEuler ?? [0, 0, 0];
  const visible = partTransformation?.visible !== false;
  return (
    <group
      {...(name ? { name } : {})}
      position={pos}
      rotation={[rot[0] * DEG2RAD, rot[1] * DEG2RAD, rot[2] * DEG2RAD, 'XYZ']}
      visible={visible}
    >
      {children}
    </group>
  );
};
